/**
 * Unified price calculation utility
 * This function ensures consistent price calculations across all screens
 * (Product Details, Cart, Shipping, Payment)
 */

/**
 * Get the display price for a product. When admin sets displayPriceType to 'bulk_max'
 * and product has bulk pricing, returns the max-quantity tier as main price; otherwise
 * returns regular (or discounted) unit price.
 * @param {Object} product - Product with price, discount?, bulkPricing?, displayPriceType?, currency?
 * @param {Object} product.productInfo - Optional (cart structure); uses productInfo.price etc.
 * @returns {{ price: number, quantity: number, isBulk: boolean, perItemPrice: number, currency: string }}
 */
export const getDisplayPrice = (product) => {
  const info = product?.productInfo || product;
  if (!info) return { price: 0, quantity: 1, isBulk: false, perItemPrice: 0, currency: '' };

  const price = Number(info.price) || 0;
  const discount = Number(info.discount) || 0;
  const currency = info.currency || '';
  const displayPriceType = info.displayPriceType || (product?.displayPriceType);
  const bulkPricing = Array.isArray(info.bulkPricing) ? info.bulkPricing : [];

  const validBulkPricing = bulkPricing.filter(
    (bp) => bp != null && bp.quantity != null && bp.price != null && bp.quantity > 0 && !isNaN(bp.quantity) && !isNaN(bp.price)
  );

  const useBulkAsMain = displayPriceType === 'bulk_max' && validBulkPricing.length > 0;

  if (useBulkAsMain) {
    const sorted = [...validBulkPricing].sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
    const maxTier = sorted[0];
    const tierPrice = Number(maxTier.price);
    const tierQty = Number(maxTier.quantity);
    return {
      price: tierPrice,
      quantity: tierQty,
      isBulk: true,
      perItemPrice: tierQty > 0 ? tierPrice / tierQty : tierPrice,
      currency,
    };
  }

  const effectivePrice = discount > 0 ? price * (1 - discount / 100) : price;
  return {
    price: effectivePrice,
    quantity: 1,
    isBulk: false,
    perItemPrice: effectivePrice,
    currency,
  };
};

/**
 * Calculate the total price for a product including discounts, bulk pricing, and addons
 * @param {Object} product - The product object
 * @param {Object} product.productInfo - Product information (for cart/shipping screens)
 * @param {number} product.productInfo.price - Base price
 * @param {number} product.productInfo.discount - Discount percentage
 * @param {Array} product.productInfo.bulkPricing - Bulk pricing options
 * @param {string} product.productInfo.currency - Currency symbol
 * @param {number} product.quantity - Quantity of the product
 * @param {Array} product.addons - Selected addons (prices are per unit; total includes × quantity)
 * 
 * OR for product details screen:
 * @param {Object} product - Direct product object
 * @param {number} product.price - Base price
 * @param {number} product.discount - Discount percentage
 * @param {Array} product.bulkPricing - Bulk pricing options
 * @param {string} product.currency - Currency symbol
 * @param {number} quantity - Quantity (separate parameter for details screen)
 * @param {Object} selectedAddons - Selected addons (separate parameter for details screen)
 * 
 * @returns {number} - Total calculated price
 */
export const calculateProductPrice = (product, quantity = null, selectedAddons = null) => {
  // Handle different data structures
  let productInfo, productQuantity, productAddons;
  
  if (product.productInfo) {
    // Cart/Shipping screen structure
    productInfo = product.productInfo;
    productQuantity = product.quantity || 1;
    productAddons = product.addons || [];
  } else {
    // Product details screen structure
    productInfo = product;
    productQuantity = quantity || 1;
    productAddons = selectedAddons || {};
  }

  if (!productInfo) return 0;

  const {
    price = 0,
    discount = 0,
    bulkPricing = [],
    currency = "NA",
  } = productInfo;

  // Ensure quantity is a valid number
  const currentQuantity = typeof productQuantity === 'string' ? parseInt(productQuantity, 10) || 1 : productQuantity;
  if (currentQuantity < 1) return 0;

  // Calculate addons price
  let addonsPrice = 0;
  
  if (Array.isArray(productAddons)) {
    // Cart/Shipping screen addons structure
    addonsPrice = productAddons.reduce((total, addon) => {
      return total + (typeof addon.price === 'number' ? addon.price : 0);
    }, 0);
  } else if (typeof productAddons === 'object' && productAddons !== null) {
    // Product details screen addons structure
    Object.entries(productAddons).forEach(([addonIndex, addon]) => {
      if (Array.isArray(addon)) {
        // Handle checkbox addons (multiple selections)
        addon.forEach(option => {
          addonsPrice += option.price || 0;
        });
      } else if (addon && addon.price > 0) {
        // Handle single selection addons
        addonsPrice += addon.price;
      }
    });
  }

  // Apply discount to base price first
  let basePrice = price;
  if (discount > 0) {
    const discountAmount = (basePrice * discount) / 100;
    basePrice = basePrice - discountAmount;
  }

  // Check for bulk pricing using tier per-item pricing
  let totalPrice = 0;
  if (Array.isArray(bulkPricing) && bulkPricing.length > 0) {
    const validBulkPricing = bulkPricing.filter(
      (bp) => bp?.quantity && bp?.price && !isNaN(bp.quantity) && !isNaN(bp.price)
    );
    
    if (validBulkPricing.length > 0) {
      // Pick the best applicable tier and apply its per-item rate to all units.
      const sortedBulkPricing = [...validBulkPricing].sort(
        (a, b) => (b.quantity || 0) - (a.quantity || 0)
      );
      const matchedTier = sortedBulkPricing.find(
        (bulkOption) => currentQuantity >= bulkOption.quantity
      );

      if (matchedTier) {
        const tierUnitPrice = Number(matchedTier.price) / Number(matchedTier.quantity);
        totalPrice = tierUnitPrice * currentQuantity;
      } else {
        totalPrice = basePrice * currentQuantity;
      }
    } else {
      // No bulk pricing, use regular price
      totalPrice = basePrice * currentQuantity;
    }
  } else {
    // No bulk pricing, use regular price
    totalPrice = basePrice * currentQuantity;
  }

  // Add-ons apply per unit: (sum of selected add-on prices) × quantity
  totalPrice += addonsPrice * currentQuantity;
  
  return totalPrice;
};

/**
 * Calculate subtotal for multiple products
 * @param {Array} products - Array of product objects
 * @returns {number} - Total subtotal
 */
export const calculateSubtotal = (products) => {
  if (!Array.isArray(products)) return 0;
  
  return products.reduce((total, product) => {
    return total + calculateProductPrice(product);
  }, 0);
};

/**
 * Calculate total order amount including shipping
 * @param {Array} products - Array of product objects
 * @param {number} shippingFee - Shipping fee
 * @returns {number} - Total order amount
 */
export const calculateOrderTotal = (products, shippingFee = 0) => {
  const subtotal = calculateSubtotal(products);
  return subtotal + shippingFee;
};
